/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';

interface SentinelEventsStatusCellProps {
  status: string;
};

enum EventStatus {
  ALERT = 'Alert',
  NORMAL = 'Normal',
  WARNING = 'Warning'
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  container : {
    height     : '100%',
    display    : 'flex',
    alignItems : 'center'
  },
  foundation : {
    backgroundColor : dlsColor('white'),
    borderRadius    : '4px'
  },
  cell : {
    ...dlsAlt('typography', 'label3'),
    color           : ({textColor}: {textColor: string}) => textColor,
    backgroundColor : ({cellBackgroundColor}: {cellBackgroundColor: string}) => `${cellBackgroundColor}`,
    fontWeight      : 500,
    fontSize        : '10px',
    lineHeight      : '16px',
    borderRadius    : '2px',
    padding         : '4px 8px',
    maxHeight       : '24px',
    minWidth        : '60px',
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center'
  }
}));

/**
 * This component renders a styled cell displaying the current status of a Sentinel event.
 * The cell's appearance and label are determined by the event's status,which is mapped
 * from the event code.
 * 
 * ```typescript
 * 
 * import SentinelEventsStatusCell from './SentinelEventsStatusCell';
 * 
 * return (
 *   <SentinelEventsStatusCell status="Alert" />
 * );
 * 
 * ```
 * 
 * Status Appearance:
 * - **Normal**  : Green background with white text
 * - **Warning** : Light orange background with orange text
 * - **Alert**   : Red background with white text
 * 
 */
export const SentinelEventsStatusCell = ({status}: SentinelEventsStatusCellProps) => {
  const statusColorMap = useMemo(() => ({
    [EventStatus.ALERT] : {
      backgroundColor : dlsColor('caRed'),
      color           : dlsColor('white')
    },
    [EventStatus.WARNING] : {
      backgroundColor : `${dlsAlt('maps', 'colors', 'lightOrange')}40`,
      color           : dlsAlt('maps', 'colors', 'orange')
    },
    [EventStatus.NORMAL] : {
      backgroundColor : dlsColor('caGreen'),
      color           : dlsColor('white')
    }
  }), []);

  const statusTranslationMap = useMemo(() => {
    return {
      [EventStatus.ALERT]   : translate('admin-devices:view.sentinel.status.alert'),
      [EventStatus.WARNING] : translate('admin-devices:view.sentinel.status.warning'),
      [EventStatus.NORMAL]  : translate('admin-devices:view.sentinel.status.normal')
    };
  }, []);

  const cellBackgroundColor = statusColorMap?.[status]?.backgroundColor;
  const textColor           = statusColorMap?.[status]?.color;
  const classes             = useStyles({cellBackgroundColor, textColor});
  
  return (
    <div className={classes.container}>
      <div className={classes.foundation}>
        <div className={classes.cell}>
          {statusTranslationMap?.[status]}
        </div>
      </div>
    </div>
  );
};

export default memo(SentinelEventsStatusCell);
